import { useEffect, useLayoutEffect, useMemo, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";

/**
 * Keeps a panel's content ALIVE while its slot comes and goes.
 *
 * The content of each panel (Monaco, xterm, the Explorer tree, the chat composer) is rendered exactly once through a
 * portal into a stable `host` element. A visible panel's slot adopts that host; when the panel is hidden, or the
 * arrangement changes (docking, another panel toggled), the host is moved back to the PARKING area. React never
 * unmounts the content, so editor tabs, unsaved edits, terminals and drafts survive.
 *
 * Why a parking area and not a detached element: xterm and Monaco measure their container. A real, sized, but
 * `visibility: hidden` and off-screen parent keeps that measurement valid, while hidden panels are neither focusable
 * nor exposed to assistive technology and take no space in the layout.
 */
const PARKING_ID = "racler-panel-parking";

function parking(): HTMLElement {
  let el = document.getElementById(PARKING_ID);
  if (!el) {
    el = document.createElement("div");
    el.id = PARKING_ID;
    el.className = "panel-parking";
    el.setAttribute("aria-hidden", "true");
    document.body.appendChild(el);
  }
  return el;
}

export function useHost(name: string): HTMLDivElement {
  const host = useMemo(() => {
    const el = document.createElement("div");
    el.className = "panel-host";
    el.dataset["panel"] = name;
    return el;
  }, [name]);
  // Parked from the start; removed again when the IDE goes away.
  useLayoutEffect(() => {
    if (!host.parentNode) parking().appendChild(host);
    return () => host.remove();
  }, [host]);
  return host;
}

/** Renders `children` into `host` (which lives outside the React-managed tree). */
export function HostPortal({ host, children }: { host: HTMLElement; children: ReactNode }) {
  return createPortal(children, host);
}

/** A place in the layout where `host` is shown. When the slot goes away the host is parked again (content stays mounted). */
export function HostSlot({ host }: { host: HTMLElement }) {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const slot = ref.current;
    if (!slot) return;
    slot.appendChild(host);
    return () => {
      if (host.parentNode === slot) parking().appendChild(host);
    };
  }, [host]);
  // Let the content re-measure once it is shown again (Monaco / xterm observe their own size).
  useEffect(() => {
    const t = window.requestAnimationFrame(() => window.dispatchEvent(new Event("resize")));
    return () => window.cancelAnimationFrame(t);
  }, [host]);
  return <div ref={ref} className="panel-slot" />;
}
